import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type ColumnDef,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
} from '@tanstack/react-table';
import { uniqueId } from 'lodash-es';
import {
  Children,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { DATA_TABLE_ROW_DETAILS_COLUMN_ID } from '../../constants.js';
import type { DataTableRowData } from '../../public.api.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type {
  DataTableExpandableRowProps,
  DataTableRowDetailState,
} from './row-details-types.js';

/**
 * DataTable slot selection for expandable rows.
 * @public
 */
export function DataTableExpandableRowTemplate<TData extends DataTableRowData>(
  props: DataTableExpandableRowProps<TData>,
) {
  return null;
}

/**
 * Type guard to ensure that a react element is a `DataTable.ExpandableRow` element.
 * @internal
 */
function isDataTableExpandableRowTemplate<TData extends DataTableRowData>(
  child: unknown,
): child is ReactElement<DataTableExpandableRowProps<TData>> {
  return isValidElement(child) && isSlot(child, DataTableExpandableRowTemplate);
}

/**
 * Helper function to identify the ROW_DETAILS_COLUMN
 * @internal
 */
export function isRowDetailsColumnDef<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: ColumnDef<TDataTableFeatures, TData, TValue>): boolean {
  return column.id === DATA_TABLE_ROW_DETAILS_COLUMN_ID;
}

/**
 * Configuration hook for expandable rows of the DataTable.
 * @internal
 */
export function useRowDetails<TData extends DataTableRowData>(
  props: PropsWithChildren,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const { children } = props;
  const rowDetailsTemplateProps = useMemo(() => {
    for (const child of Children.toArray(children)) {
      if (isDataTableExpandableRowTemplate<TData>(child)) {
        return child?.props;
      }
    }
  }, [children]);

  const [openRowDetails, setOpenRowDetails] = useControllableState({
    value: rowDetailsTemplateProps?.expandedRows,
    defaultValue: rowDetailsTemplateProps?.defaultExpandedRows,
    onChange: (value) => {
      if (value !== true) {
        rowDetailsTemplateProps?.onExpandedRowsChange?.(value);
      }
    },
  });

  const template = rowDetailsTemplateProps?.children;
  if (template !== undefined && typeof template === 'function') {
    // oxlint-disable-next-line react/immutability
    options.enableRowDetails = true;
    // Cast is necessary to align the public template interface with the internal rowData
    // oxlint-disable-next-line react/immutability
    options.rowDetailsTemplate = template as (params: {
      row: RowData;
    }) => ReactNode;
    // Cast is necessary to align the public template interface with the internal rowData
    // oxlint-disable-next-line react/immutability
    options.rowDetailsDisableRow = rowDetailsTemplateProps?.disableExpand as (
      row: DataTableRowData,
    ) => boolean;

    // oxlint-disable-next-line react/immutability
    options.state = {
      ...options.state,
      rowDetails: openRowDetails,
    };
    // oxlint-disable-next-line react/immutability
    options.onRowDetailsOpenChange = setOpenRowDetails;
  }
}

/**
 * Allows access to the row model coming before the row details.
 */
export function table_getPreRowDetailsRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  return table.getPaginatedRowModel();
}

/**
 * Allows access to the row details model if available.
 */
export function table_getRowDetailsRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  table._rowModels.detailsRowModel ??= table.options.detailsRowModel?.(table);

  if (!table.options.enableRowDetails || !table._rowModels.detailsRowModel) {
    return table.getPreRowDetailsRowModel();
  }

  return table._rowModels.detailsRowModel();
}

/**
 * Helper to get a unique id for the table instance.
 * @internal
 */
export function table_rowDetailsTableId<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  if (!table._rowDetailsTableId) {
    table._rowDetailsTableId = uniqueId('DataTable-');
  }
  return table._rowDetailsTableId;
}

/**
 * Determines if the details row for a particular row is open.
 * @internal
 */
export function row_getIsDetailsOpen<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
): boolean {
  const detailOpenState = row.table.atoms.rowDetails?.get() ?? false;

  if (typeof detailOpenState === 'boolean') {
    return detailOpenState;
  }
  return detailOpenState[row.id] === true;
}

/**
 * Defines the grid position for the row details row.
 * @internal
 */
export function row_getRowDetailsGridPosition<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
) {
  return {
    gridColumnStart: 1,
    gridColumnEnd: `span ${row.table.getVisibleLeafColumns().length}`,
  };
}

/**
 * Returns the aria control id to connect the expanding button to the
 * row details wrapper.
 * @internal
 */
export function row_getRowDetailsAriaControlsId<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
): string {
  return `${row.table.rowDetailsTableId()}-${row.id}-expand`;
}

/**
 * Determines if the details for this row can be shown.
 * @internal
 */
export function row_getCanOpenDetails<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
): boolean {
  if (typeof row.table.options.rowDetailsDisableRow === 'function') {
    return !row.table.options.rowDetailsDisableRow(row.original);
  }
  return true;
}

/**
 * Control command to toggle the state of the rowDetails.
 * @internal
 */
export function row_toggleDetails<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
) {
  // remember current scroll position
  const scrollOffset = row.table.getRowScrollOffset();

  // expand/collapse details
  row.table.options.onRowDetailsOpenChange?.(
    (prev: DataTableRowDetailState) => {
      let newRowDetailsState: DataTableRowDetailState = {};
      if (typeof prev === 'boolean' && prev === true) {
        const flatRows = row.table.getRowModel().flatRows;
        for (const flatRow of flatRows) {
          if (row.getCanOpenDetails() && row.id !== flatRow.id) {
            newRowDetailsState[flatRow.id] = true;
          }
        }
        return newRowDetailsState;
      }

      newRowDetailsState = { ...prev };
      if (newRowDetailsState[row.id] === true) {
        delete newRowDetailsState[row.id];
      } else {
        newRowDetailsState[row.id] = true;
      }
      return newRowDetailsState;
    },
  );

  // scroll back to original scroll position to avoid row jumping
  requestAnimationFrame(() => {
    row.table.scrollToRowOffset(scrollOffset);
  });
}

/**
 * Constructs the column row details for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowDetailsFeature(): TableFeature {
  return {
    getInitialState: (initialState) => {
      return {
        rowDetails: {},
        ...initialState,
      };
    },
    assignRowPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableRowDetailsFeature', prototype, table, {
          row_getIsDetailsOpen: { fn: (row) => row_getIsDetailsOpen(row) },
          row_getRowDetailsGridPosition: {
            fn: (row) => row_getRowDetailsGridPosition(row),
          },
          row_getRowDetailsAriaControlsId: {
            fn: (row) => row_getRowDetailsAriaControlsId(row),
          },
          row_getCanOpenDetails: { fn: (row) => row_getCanOpenDetails(row) },
          row_toggleDetails: { fn: (row) => row_toggleDetails(row) },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableRowDetailsFeature', table, {
          table_rowDetailsTableId: {
            fn: () => table_rowDetailsTableId(table),
          },
          table_getPreRowDetailsRowModel: {
            fn: () => table_getPreRowDetailsRowModel(table),
          },
          table_getRowDetailsRowModel: {
            fn: () => table_getRowDetailsRowModel(table),
          },
          table_getRowModel: {
            fn: () => table_getRowDetailsRowModel(table),
          },
        });
      },
    ),
  };
}

/**
 * Feature implementation for Row Details.
 * @internal
 */
export const DataTableRowDetailsFeature = constructRowDetailsFeature();
